import { supabase } from '../config/supabase';

// Production-ready token generation using Supabase Edge Functions
export const getToken = async (roomName: string, participantName: string): Promise<string> => {
  console.log('[LiveKitToken] Requesting token for room:', roomName, 'participant:', participantName);
  
  try {
    // Validate inputs
    if (!roomName || !participantName) {
      throw new Error('Room name and participant name are required');
    }

    // Get the current user's session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('[LiveKitToken] Session error:', sessionError);
      throw new Error('Authentication session error: ' + sessionError.message);
    }
    
    if (!session) {
      throw new Error('User must be authenticated to join voice chat. Please sign in first.');
    }

    console.log('[LiveKitToken] User authenticated:', session.user.id);

    // Call Supabase Edge Function with timeout
    console.log('[LiveKitToken] Calling Edge Function...');
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Token request timed out after 10 seconds')), 10000)
    );

    const functionPromise = supabase.functions.invoke('livekit-token', {
      body: { roomName, participantName }
    });

    const { data, error } = await Promise.race([functionPromise, timeoutPromise]) as any;

    if (error) {
      console.error('[LiveKitToken] Edge function error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to get voice chat token';
      if (error.message?.includes('timeout')) {
        errorMessage = 'Voice chat service is taking too long to respond. Please try again.';
      } else if (error.message?.includes('not found') || error.message?.includes('404')) {
        errorMessage = 'Voice chat service not available. The LiveKit Edge Function may not be deployed.';
      } else if (error.message?.includes('unauthorized') || error.message?.includes('403')) {
        errorMessage = 'Permission denied. Please check your account permissions.';
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        errorMessage = 'Network error connecting to voice chat service. Please check your connection.';
      } else if (error.message) {
        errorMessage = 'Voice chat error: ' + error.message;
      }
      
      throw new Error(errorMessage);
    }

    if (!data) {
      throw new Error('No response from voice chat service. The service may be down.');
    }

    if (!data.token) {
      console.error('[LiveKitToken] Invalid response:', data);
      throw new Error('Invalid token response from voice chat service');
    }

    console.log('[LiveKitToken] Token generated successfully');
    return data.token;
  } catch (error) {
    console.error('[LiveKitToken] Token generation error:', error);
    
    // Re-throw with user-friendly message
    if (error instanceof Error) {
      throw error;
    }
    
    throw new Error('Unexpected error generating voice chat token');
  }
};

// Helper function to validate LiveKit configuration
export const validateLiveKitConfig = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  const livekitUrl = import.meta.env.VITE_LIVEKIT_URL;
  if (!livekitUrl) {
    errors.push('VITE_LIVEKIT_URL is not configured');
  } else if (!livekitUrl.startsWith('wss://')) {
    errors.push('VITE_LIVEKIT_URL must be a secure WebSocket URL (wss://)');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};